// ============================================================
// COMBAT DATA & CONSTANTS - Pure data + lookup functions
// ============================================================
import { rollD100 } from './dice';
import type { HitLocation } from '../types';

export const HIT_LOCATIONS: HitLocation[] = [
  { id: 'head', name: 'Head', min: 1, max: 10 },
  { id: 'rightArm', name: 'Right Arm', min: 11, max: 20 },
  { id: 'leftArm', name: 'Left Arm', min: 21, max: 30 },
  { id: 'body', name: 'Body', min: 31, max: 70 },
  { id: 'rightLeg', name: 'Right Leg', min: 71, max: 85 },
  { id: 'leftLeg', name: 'Left Leg', min: 86, max: 100 },
];

export function getHitLocation(roll: number): HitLocation {
  const s = roll.toString().padStart(2, '0');
  const reversed = parseInt(s[1] + s[0]);
  const val = reversed === 0 ? 100 : reversed;
  for (const loc of HIT_LOCATIONS) {
    if (val >= loc.min && val <= loc.max) return loc;
  }
  return HIT_LOCATIONS[3];
}

export function getRandomHitLocation(): { loc: HitLocation; roll: number } {
  const roll = rollD100();
  for (const loc of HIT_LOCATIONS) {
    if (roll >= loc.min && roll <= loc.max) return { loc, roll };
  }
  return { loc: HIT_LOCATIONS[3], roll };
}

export function calculateHits(dos: number, mode: string, rofValue: number): number {
  if (mode === 'single') return 1;
  if (mode === 'semi' || mode === 'swift') return Math.min(1 + Math.floor(dos / 2), rofValue);
  if (mode === 'full' || mode === 'lightning') return Math.min(1 + dos, rofValue);
  return 1;
}

/**
 * The size ladder, smallest first. SIZE_MODS is the same fact as a
 * to-hit number; this is it as an ordered scale, for rules that count
 * CATEGORIES (Titanbane's +X per step above Average, v8.285.0).
 */
export const SIZE_ORDER: readonly string[] = [
  'Minuscule',
  'Puny',
  'Scrawny',
  'Average',
  'Hulking',
  'Enormous',
  'Massive',
];

export const SIZE_MODS: Record<string, number> = {
  Minuscule: -30,
  Puny: -20,
  Scrawny: -10,
  Average: 0,
  Hulking: 10,
  Enormous: 20,
  Massive: 30,
};

/**
 * How tall each size category stands, in METRES.
 *
 * Beside SIZE_MODS because it is the same fact seen from a different
 * angle: a Massive creature is easier to hit AND harder to hide behind a
 * wall, for the one reason that it is enormous.
 *
 * The battlefield derives two things from this - how far off the ground a
 * combatant's eyes are, and how much of them a given obstacle hides. See
 * `engine/battlefield/coverModel.ts`.
 */
export const SIZE_HEIGHTS: Record<string, number> = {
  Minuscule: 0.3, // servo-skull, rat
  Puny: 0.9, // grot, child
  Scrawny: 1.4, // gaunt, eldar
  Average: 1.8, // human, guardsman
  Hulking: 2.4, // Space Marine, ork nob
  Enormous: 3.5, // Dreadnought, carnifex
  Massive: 6.0, // greater daemon, walker
};

export const COVER_LEVELS: Record<string, string[]> = {
  none: [],
  low: ['rightLeg', 'leftLeg'],
  half: ['body', 'rightLeg', 'leftLeg'],
  threeQuarter: ['rightArm', 'leftArm', 'body', 'rightLeg', 'leftLeg'],
  full: ['head', 'rightArm', 'leftArm', 'body', 'rightLeg', 'leftLeg'],
};

export const COVER_LABELS: Record<string, string> = {
  none: 'None',
  low: 'Low (Legs)',
  half: 'Half (Body+Legs)',
  threeQuarter: '\u00BE (All but Head)',
  full: 'Full (All)',
};

export function isLocCovered(level: string, locId: string): boolean {
  return (COVER_LEVELS[level] || []).includes(locId);
}

export const DAMAGE_TYPE_COLORS: Record<string, { bg: string; text: string; label: string }> = {
  I: { bg: '#6b7280', text: '#fff', label: 'Impact' },
  E: { bg: '#f59e0b', text: '#000', label: 'Energy' },
  R: { bg: '#ef4444', text: '#fff', label: 'Rending' },
  X: { bg: '#7c2d12', text: '#fff', label: 'Explosive' },
  A: { bg: '#8b5cf6', text: '#fff', label: 'Any' },
};

// Location id→name lookup
export const LOC_NAMES: Record<string, string> = {};
HIT_LOCATIONS.forEach((l) => {
  LOC_NAMES[l.id] = l.name;
});

// Defence prompt timeout - auto-cleared after this duration
export const DEFENCE_TIMEOUT_MS = 120_000; // 2 minutes

// Default permission locks for non-GM players in multiplayer sessions
export const DEFAULT_LOCKS: Record<string, boolean> = {
  undo: true,
  editTarget: true,
  /**
   * Editing what a character IS, live: characteristics, skills, talents,
   * traits, pristine armour (v8.405.0). Apart from `editTarget`, which
   * gates what has HAPPENED to them (wounds, conditions, cover), and far
   * more powerful, so it is its own lock and closed by default.
   */
  editSheets: true,
  resetTarget: true,
  attack: false,
  editProfiles: false,
  manageTabs: true,
  targetTemplates: true,
  characterLibrary: true,
  turnTracker: false,
  /**
   * Deployment: planting the marker and ringing people around it.
   *
   * Locked for players by default, like the rest of the map's authoring.
   * Unlike Build - which is GM-only with no way to grant it at all - this
   * one is a real lock, so "the GM and anyone the GM allows" is true of
   * it. Build could adopt the same key whenever that is wanted.
   */
  deployment: true,
  /**
   * Placing a marker on a storey without paying for the climb.
   *
   * LOCKED FOR PLAYERS by default. It is an authoring power: somebody
   * who can reach a roof for nothing is somebody the climb rules do not
   * apply to. The GM always has it - `isLocked` returns false for them
   * whatever this says - and can grant it per player like any other.
   */
  freePlacement: true,
  /**
   * Seeing the waiting rooms, and deploying from them.
   *
   * LOCKED FOR PLAYERS, and unlike every other key here that is not
   * merely a UI decision: `firestore.rules` reads this same map, so a
   * player without the grant cannot read the documents at all. The
   * whole point of a waiting room is that nobody sees what is about to
   * come round the corner.
   */
  waitingRoom: true,
  /**
   * Seeing and editing the Protagonist tab.
   *
   * LOCKED FOR PLAYERS by default, and a real lock rather than a GM-only
   * surface, so "the GM and anyone the GM allows" is true of it - a
   * co-GM should be able to set a character up.
   *
   * A UI decision, not an enforced one: the values live on the character
   * sheet, which is `access: 'member'`, so a player with a console can
   * read them. That is accepted - hiding the control is what stops a
   * player idly clicking through an ally's sheet and finding out.
   */
  protagonistTab: true,
  /**
   * The vox (v8.363.6). Talking at the table and whispering are OPEN by
   * default - speech, like a ping. The GM may close either: `chat` refuses
   * every line, `chatWhisper` only the private ones. Neither is read by the
   * rules; a closed vox is a UI refusal with a sentence.
   */
  chat: false,
  chatWhisper: false,
  /**
   * Pinging the map.
   *
   * UNLOCKED, unlike every other key here. A ping is speech: it writes
   * nothing, spends nothing and is gone in half a second, and a table
   * where the players cannot point at the map is the situation this
   * feature exists to end. The lock is here for the GM who finds it
   * noisy, not as a default posture.
   */
  ping: false,
};
